import { DownloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Image } from "antd";
import { STAMPS_UPLOADS_URL } from "common/api";
import { TapAnimationComponent } from "components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { stampService } from "services";
import { message } from "antd";
import { API, COMMONS } from "common";
import { Modal } from "antd";
import fileDownload from "js-file-download";
import { useAdminLayoutContext } from "layouts/AdminMain/AdminMainContext";
import { DATE_RAW_FORMAT, JP_DATE_TIME_FORMAT, LIFF_URI } from "common/common";
import { formatDate } from "common/datetime";

const ViewDetailStamp = (props) => {
	const { data } = props;
	const queryClient = useQueryClient();
	const [modal, contextHolder] = Modal.useModal();
	const { isBlock } = useAdminLayoutContext();

	const { mutateAsync: resetStamp, isLoading: isResettingStamp } = useMutation({
		mutationFn: async () => {
			try {
				await stampService.reset();

				message.success(COMMONS.SUCCESS_DELETE_MSG);

				queryClient.invalidateQueries([API.QUERY_KEY_ADMIN_STAMPS]);
			} catch (error) {
				console.log("error :>> ", error);
			}
		},
	});

	const handleCSVFile = async (csvText) => {
		try {
			const stampName = data?.name;
			const today = formatDate(new Date());
			const fileName = `${stampName}_${today}.csv`;
			fileDownload("\uFEFF" + csvText, fileName, "text/csv");
		} catch (error) {
			console.error(error);
		}
	};

	const downloadCSV = useMutation({
		mutationFn: async () => {
			try {
				const response = await stampService.downloadCSV();
				handleCSVFile(response);
				queryClient.invalidateQueries([API.QUERY_KEY_ADMIN_STAMPS]);
			} catch (error) {
				console.log("error :>> ", error);
			}
		},
	});

	const handleDeleteStamp = () => {
		modal.confirm({
			title: "リセットしてよろしいですか？",
			icon: <ExclamationCircleOutlined className="text-red-600" />,
			content: (
				<div>
					{/* <div className='font-bold text-base'>リセットしてよろしいですか？</div> */}
					<div className="text-xs">
						今のスタンプラリーへの参加情報がすべてリセットされます。
						<br />
						お客様リストCSVをダウンロードしてからリセットしてください。
					</div>
					<div className="text-[#A8A8A8] text-xs">
						※CSVダウンロード後にスタンプラリー利用状況が変化している可能性もありますので、ご注意ください。
					</div>
				</div>
			),
			okText: "リセット",
			okButtonProps: {
				size: "large",
				type: "primary",
				danger: true,
				style: {
					minWidth: 111,
				},
				loading: isResettingStamp,
			},
			cancelText: "キャンセル",
			cancelButtonProps: {
				size: "large",
				style: {
					minWidth: 80,
				},
			},
			centered: true,
			onOk: () => resetStamp(),
		});
	};

	return (
		<div>
			{contextHolder}
			<div className="flex justify-between items-center mb-4 py-[23px] px-[29px] border-b border-[#cfcfcf]">
				<div className="text-[18px] font-bold">スタンプラリー</div>
				<div>
					<TapAnimationComponent className="inline-block">
						<Button
							loading={downloadCSV.isLoading}
							onClick={() => downloadCSV.mutate()}
							size="large"
							disabled={isBlock(COMMONS.ACTION_KEY.DOWNLOAD_CSV_STAMP)}
						>
							<DownloadOutlined /> お客様リストCSV
						</Button>
					</TapAnimationComponent>
				</div>
			</div>
			<div className="text-sm flex flex-col gap-y-[27px] mx-[29px] pb-5 text-black">
				<div className="flex items-start">
					<div className="font-bold w-[200px] min-w-[200px] break-word">
						スタンプラリー名
					</div>
					<div className="break-word">{data?.name}</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] max-w-[200px] min-w-[200px]">
						説明テキスト
					</div>
					<div className="whitespace-pre-wrap break-word leading-[24px] max-w-[800px]">
						{data?.description}
					</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] min-w-[200px]">イベント期間</div>
					<div className=" whitespace-pre-wrap leading-[24px] max-w-[800px]">
						{formatDate(data?.fromDate, JP_DATE_TIME_FORMAT)}　～　
						{formatDate(data?.toDate, JP_DATE_TIME_FORMAT)}
					</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] min-w-[200px]">
						スタンプラリーの数
					</div>
					<div className=" whitespace-pre-wrap leading-[24px] max-w-[800px]">
						<div>{data?.totalStamp}</div>
						<div className="text-xs">QRコード作成用URL</div>
						{data?.StampImages?.map((item, index) => {
							const term = `${formatDate(data?.fromDate, DATE_RAW_FORMAT)}_${formatDate(data?.toDate, DATE_RAW_FORMAT)}`;

							const scanStampUrl = `${LIFF_URI}?no=${index + 1}&term=${term}&stampImageId=${item?.stampImagesId}`;

							return (
								<div className="flex items-center gap-x-[5px]" key={index}>
									<div className="size-[13px] flex justify-center items-center text-[10px] leading-none border rounded-full">
										{index + 1}
									</div>
									<div>{scanStampUrl}</div>
								</div>
							);
						})}
					</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] min-w-[200px]">スタンプ画像</div>
					<div className="flex gap-[25px] flex-wrap">
						{data?.StampImages?.map((item, index) => {
							const fileName = item?.imageUrl;

							return (
								<div className="flex items-center gap-x-[5px]" key={index}>
									<div>{index + 1} : </div>
									<Image
										src={`${STAMPS_UPLOADS_URL}${fileName}`}
										className="size-[78px] object-fill rounded-full"
									/>
								</div>
							);
						})}
					</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] min-w-[200px] whitespace-pre">{`ゴール達成時\n通知メッセージ`}</div>
					<div className="break-word whitespace-pre-wrap leading-[24px] max-w-[800px]">
						{data?.ultimateGoalMess}
					</div>
				</div>

				<div className="flex items-start pb-[22px]">
					<div className="font-bold w-[200px] min-w-[200px]">中間ゴール</div>
					<div>{data?.midtermGoal}</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] min-w-[200px] whitespace-pre">{`中間ゴール達成時\n通知メッセージ`}</div>
					<div className="break-word whitespace-pre-wrap leading-[24px] max-w-[800px]">
						{data?.midtermGoalMess}
					</div>
				</div>

				<div className="flex items-start border-b border-[#cfcfcf] pb-[30px]">
					<div className="font-bold w-[200px] min-w-[200px] whitespace-pre">
						注釈
					</div>
					<div className="break-word whitespace-pre-wrap leading-[24px] max-w-[800px]">
						{data?.note}
					</div>
				</div>

				<div className="flex justify-end">
					<TapAnimationComponent className="inline-block">
						<Button
							size="large"
							className={`${isBlock(COMMONS.ACTION_KEY.RESET_STAMP) ? "bg-gray-100 text-gray-400" : "bg-[#FF0000] text-white"}`}
							onClick={handleDeleteStamp}
							loading={isResettingStamp}
							disabled={isBlock(COMMONS.ACTION_KEY.RESET_STAMP)}
						>
							スタンプラリーをリセット
						</Button>
					</TapAnimationComponent>
				</div>
			</div>
		</div>
	);
};
export default ViewDetailStamp;
